<template>
  <div class="list-page-container">
    <!-- 顶部操作区：副标题与操作按钮 -->
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-upload"></i>
          <span>{{ $t('device.deviceUpgradeSubtitle') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button type="primary" @click="handleStartUpgrade" v-if="hasPermission('sys:ota:save')">
            <i class="el-icon-upload2"></i>{{ $t('device.startUpgrade') }}
          </el-button>
          <el-button @click="handleRefresh">
            <i class="el-icon-refresh"></i>{{ $t('common.refresh') }}
          </el-button>
          <el-button type="success" @click="handleExportHistory" :loading="exportLoading" v-if="hasPermission('sys:ota:list')">
            <i class="el-icon-download"></i>{{ $t('common.export') }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 统一控制中心：筛选模块 -->
    <div class="control-panel has-query">
      <div class="control-container">
        <el-form :model="filterForm" :inline="true" label-position="left" label-width="80px" class="filter-form">
          <el-form-item>
            <el-input
              v-model="filterForm.keywords"
              :placeholder="$t('device.deviceNamePlaceholder')"
              clearable
              class="filter-input"
              @keyup.enter.native="handleSearch"
          >
            <i slot="prefix" class="el-input__icon el-icon-search"></i>
          </el-input>
        </el-form-item>
        
        <el-form-item>
          <el-select v-model="filterForm.status" :placeholder="$t('device.deviceStatus')" clearable class="filter-select">
            <i slot="prefix" class="el-input__icon el-icon-connection"></i>
            <el-option :label="$t('device.online')" :value="1"></el-option>
            <el-option :label="$t('device.offline')" :value="0"></el-option>
            <el-option :label="$t('device.upgrading')" :value="3"></el-option>
            <el-option :label="$t('device.error')" :value="2"></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item class="search-btns">
          <el-button type="primary" @click="handleSearch" icon="el-icon-search">
            {{ $t('common.search') }}
          </el-button>
          <el-button @click="handleResetFilter" icon="el-icon-refresh">
            {{ $t('common.reset') }}
          </el-button>
        </el-form-item>
        </el-form>
      </div>
    </div>
    
    <!-- 设备列表 -->
    <el-card class="list-card">
      <el-table
        :data="deviceList"
        stripe
        border
        v-loading="loading"
        height="100%"
        :element-loading-text="$t('common.loading')"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55" align="center"></el-table-column>
        <el-table-column prop="id" :label="$t('common.id')" width="80" align="center"></el-table-column>
        <el-table-column prop="deviceName" :label="$t('device.deviceName')" width="200" align="center">
          <template slot-scope="scope">
            <div class="device-name" @click="handleViewDetail(scope.row)">
              <strong>{{ scope.row.deviceName }}</strong>
              <el-tag v-if="scope.row.status === 'online'" type="success" size="mini" style="margin-left: 5px">
                {{ $t('device.online') }}
              </el-tag>
              <el-tag v-else-if="scope.row.status === 'offline'" type="info" size="mini" style="margin-left: 5px">
                {{ $t('device.offline') }}
              </el-tag>
              <el-tag v-else-if="scope.row.status === 'upgrading'" type="warning" size="mini" style="margin-left: 5px">
                {{ $t('device.upgrading') }}
              </el-tag>
              <el-tag v-else type="danger" size="mini" style="margin-left: 5px">
                {{ $t('device.error') }}
              </el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="macAddress" :label="$t('device.macAddress')" width="180" align="center"></el-table-column>
        <el-table-column prop="board" :label="$t('device.deviceModel')" width="150" align="center">
          <template slot-scope="scope">
            {{ getDeviceTypeLabel(scope.row.board) }}
          </template>
        </el-table-column>
        <el-table-column prop="appVersion" :label="$t('device.firmwareVersion')" width="150" align="center"></el-table-column>
        <el-table-column prop="lastUpgradeTime" :label="$t('device.lastUpgradeTime')" width="180" align="center"></el-table-column>
        <el-table-column prop="upgradeStatus" :label="$t('device.upgradeStatus')" width="120" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.upgradeStatus === 'success'" type="success" size="small">
              {{ $t('device.upgradeSuccess') }}
            </el-tag>
            <el-tag v-else-if="scope.row.upgradeStatus === 'failed'" type="danger" size="small">
              {{ $t('device.upgradeFailed') }}
            </el-tag>
            <el-tag v-else-if="scope.row.upgradeStatus === 'in_progress'" type="warning" size="small">
              {{ $t('device.upgradeInProgress') }}
            </el-tag>
            <el-tag v-else type="info" size="small">
              {{ $t('device.upgradeNotStarted') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column v-if="hasAnyActionPermission" :label="$t('common.actions')" :width="actionColumnWidth" align="center" fixed="right">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button
                type="primary"
                size="mini"
                plain
                @click="handleUpgradeSingle(scope.row)"
                :aria-label="$t('device.upgradeDevice')"
                v-if="hasPermission('sys:ota:save')"
              >
                {{ $t('device.upgradeDevice') }}
              </el-button>
              <el-button
                size="mini"
                type="primary"
                plain
                @click="handleViewDetail(scope.row)"
                :aria-label="$t('common.detail')"
                v-if="hasPermission('sys:device:info')"
              >
                {{ $t('common.detail') }}
              </el-button>
              <el-button
                size="mini"
                type="primary"
                plain
                @click="handleViewLogs(scope.row)"
                :aria-label="$t('common.logs')"
                v-if="hasPermission('sys:ota:list')"
              >
                {{ $t('common.logs') }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
        ></el-pagination>
      </div>
    </el-card>
    
    <!-- 固件上传对话框 -->
    <el-dialog 
      :title="$t('device.uploadFirmware')" 
      :visible.sync="uploadDialogVisible"
      width="500px"
    >
      <el-form :model="uploadForm" label-width="120px">
        <el-form-item :label="$t('device.firmwareVersion')">
          <el-input v-model="uploadForm.version" :placeholder="$t('device.firmwareVersionPlaceholder')"></el-input>
        </el-form-item>
        <el-form-item :label="$t('device.firmwareFile')">
          <el-upload
            class="upload-demo"
            drag
            action=""
            :before-upload="beforeUpload"
            :limit="1"
            :on-exceed="handleExceed"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">{{ $t('device.dragUploadText') }}</div>
            <div class="el-upload__tip" slot="tip">{{ $t('device.uploadTip') }}</div>
          </el-upload>
        </el-form-item>
        <el-form-item :label="$t('common.description')">
          <el-input 
            type="textarea" 
            v-model="uploadForm.description" 
            :placeholder="$t('common.descriptionPlaceholder')"
            :rows="4"
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="uploadDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="handleUploadFirmware">{{ $t('common.upload') }}</el-button>
      </div>
    </el-dialog>
    
    <!-- 升级进度对话框 -->
    <el-dialog 
      :title="$t('device.upgradeProgress')" 
      :visible.sync="upgradeProgressDialogVisible"
      width="600px"
      :close-on-click-modal="false"
    >
      <div class="upgrade-progress-container">
        <el-progress :percentage="upgradeProgress" :status="upgradeStatus === 'success' ? 'success' : upgradeStatus === 'error' ? 'exception' : ''"></el-progress>
        <div class="progress-text">
          <p v-if="upgradeStatus === 'in_progress'">{{ $t('device.upgradingDevice') }} {{ upgradeProgress }}%</p>
          <p v-else-if="upgradeStatus === 'success'" class="success-text">{{ $t('device.upgradeCompletedSuccessfully') }}</p>
          <p v-else-if="upgradeStatus === 'error'" class="error-text">{{ $t('device.upgradeFailedMessage') }}</p>
        </div>
        <div v-if="upgradeLogs.length" class="upgrade-logs">
          <h4>{{ $t('device.upgradeLogs') }}</h4>
          <div class="log-content">
            <p v-for="(log, index) in upgradeLogs" :key="index">{{ log }}</p>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="upgradeProgressDialogVisible = false">{{ $t('common.close') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getDeviceList, startDeviceUpgrade, getUpgradeHistory } from '@/apis/device/device'
import { hasPermission } from '@/utils/permission'

export default {
  name: 'DeviceUpgrade',
  data() {
    return {
      loading: false,
      exportLoading: false,
      deviceList: [],
      selectedDevices: [],
      filterForm: {
        keywords: '',
        status: ''
      },
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      uploadDialogVisible: false,
      uploadForm: {
        version: '',
        file: null,
        description: ''
      },
      upgradeProgressDialogVisible: false,
      upgradeProgress: 0,
      upgradeStatus: 'not_started',
      upgradeLogs: [],
      firmwareTypes: [
        { key: 'esp32', name: this.$t('device.typeESP32') },
        { key: 'esp32s3', name: this.$t('device.typeESP32S3') },
        { key: 'esp32c3', name: this.$t('device.typeESP32C3') }
      ]
    }
  },
  computed: {
    // 操作列权限判断
    canUpgradeDevice() {
      return this.hasPermission('sys:ota:save')
    },
    canViewDevice() {
      return this.hasPermission('sys:device:info')
    },
    canViewLogs() {
      return this.hasPermission('sys:ota:list')
    },
    // 操作列可见按钮数量（基于权限）
    visibleActionCount() {
      let count = 0
      if (this.canUpgradeDevice) count += 1
      if (this.canViewDevice) count += 1
      if (this.canViewLogs) count += 1
      return count
    },
    // 是否有任何操作权限
    hasAnyActionPermission() {
      return this.visibleActionCount > 0
    },
    // 操作列动态宽度
    actionColumnWidth() {
      const count = this.visibleActionCount
      const widthMap = { 1: 100, 2: 170, 3: 240 }
      return widthMap[count] || 0
    }
  },
  mounted() {
    this.fetchDeviceList()
  },
  beforeDestroy() {
    // 清理升级进度模拟定时器，防止组件销毁后定时器持续运行
    if (this._upgradeInterval) {
      clearInterval(this._upgradeInterval)
      this._upgradeInterval = null
    }
  },
  methods: {
    hasPermission,
    async fetchDeviceList() {
      try {
        this.loading = true
        const params = {
          page: this.pagination.currentPage,
          limit: this.pagination.pageSize,
          ...this.filterForm
        }
        const response = await getDeviceList(params)
        if (response) {
          this.deviceList = response.list || []
          this.pagination.total = response.total || 0
        }
        this.loading = false
      } catch (error) {
        console.error('获取设备列表失败:', error)
        this.$message.error(this.$t('common.fetchFailed'))
        this.loading = false
      }
    },
    
    handleSearch() {
      this.pagination.currentPage = 1
      this.fetchDeviceList()
    },
    
    handleResetFilter() {
      this.filterForm = {
        keywords: '',
        status: ''
      }
      this.pagination.currentPage = 1
      this.fetchDeviceList()
    },
    
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.fetchDeviceList()
    },
    
    handleCurrentChange(val) {
      this.pagination.currentPage = val
      this.fetchDeviceList()
    },
    
    handleSelectionChange(val) {
      this.selectedDevices = val
    },
    
    handleStartUpgrade() {
      this.uploadDialogVisible = true
    },
    
    beforeUpload(file) {
      const isValidType = file.type === 'application/octet-stream' || file.name.endsWith('.bin') || file.name.endsWith('.hex')
      if (!isValidType) {
        this.$message.error(this.$t('device.invalidFileType'))
        return false
      }
      
      const isLt50M = file.size / 1024 / 1024 < 50
      if (!isLt50M) {
        this.$message.error(this.$t('device.fileSizeExceeded'))
        return false
      }
      
      this.uploadForm.file = file
      return false
    },
    
    handleExceed() {
      this.$message.warning(this.$t('device.uploadLimitExceeded'))
    },
    
    async handleUploadFirmware() {
      try {
        if (!this.uploadForm.version) {
          this.$message.error(this.$t('device.versionRequired'))
          return
        }
        
        if (!this.uploadForm.file) {
          this.$message.error(this.$t('device.fileRequired'))
          return
        }
        
        // 模拟上传
        this.uploadDialogVisible = false
        this.$message.success(this.$t('device.uploadSuccess'))
        this.fetchDeviceList()
        
        // 重置表单
        this.uploadForm = {
          version: '',
          file: null,
          description: ''
        }
      } catch (error) {
        this.$message.error(this.$t('common.operationFailed'))
      }
    },
    
    async handleUpgradeSingle(device) {
      try {
        this.upgradeProgressDialogVisible = true
        this.upgradeProgress = 0
        this.upgradeStatus = 'in_progress'
        this.upgradeLogs = []
        
        // 模拟升级过程
        this._upgradeInterval = setInterval(() => {
          this.upgradeProgress += 10
          this.upgradeLogs.push(`[${new Date().toLocaleTimeString()}] ${this.$t('device.upgradeProgressLog')}: ${this.upgradeProgress}%`)

          if (this.upgradeProgress >= 100) {
            clearInterval(this._upgradeInterval)
            this._upgradeInterval = null
            this.upgradeStatus = 'success'
            this.upgradeLogs.push(`[${new Date().toLocaleTimeString()}] ${this.$t('device.upgradeCompleted')}`)
          }
        }, 1000)
        
        // 实际项目中，这里应该调用API
        // await startDeviceUpgrade(device.id)
      } catch (error) {
        this.upgradeStatus = 'error'
        this.$message.error(this.$t('device.upgradeFailedMessage'))
      }
    },
    
    handleViewDetail(device) {
      this.$router.push({ name: 'DeviceDetail', params: { id: device.id } })
    },
    
    handleViewLogs(device) {
      this.$router.push({ name: 'DeviceLogs', query: { deviceId: device.id } })
    },
    
    handleRefresh() {
      this.fetchDeviceList()
    },
    
    async handleExportHistory() {
      try {
        this.exportLoading = true
        // 实际项目中，这里应该调用API获取导出数据
        this.$message.success(this.$t('common.exportSuccess'))
        this.exportLoading = false
      } catch (error) {
        this.$message.error(this.$t('common.exportFailed'))
        this.exportLoading = false
      }
    },
    getDeviceTypeLabel(type) {
      if (!type) return type;
      const firmwareType = this.firmwareTypes.find(item => item.key === type)
      return firmwareType ? firmwareType.name : type
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/list-page.scss";

/* 设备升级页面特有样式 */
.list-page-container {
  .filter-input {
    width: 200px;
  }

  .filter-select {
    width: 150px;
  }

  .device-name {
    cursor: pointer;
  }

  .upload-demo {
    width: 100%;
  }
}
</style>